import { useState, useEffect } from 'react';
import type { PersonCount, ServiceType, Timing, WhatsAppData } from '@/pages/home/hooks/useWhatsApp';

interface WhatsAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: WhatsAppData;
  updateData: (updates: Partial<WhatsAppData>) => void;
  generateLink: () => string;
}

export default function WhatsAppModal({ isOpen, onClose, data, updateData, generateLink }: WhatsAppModalProps) {
  const [step, setStep] = useState(1);

  const personOptions: { value: PersonCount; label: string }[] = [
    { value: '20-50', label: '20–50 Kişi' },
    { value: '50-100', label: '50–100 Kişi' },
    { value: '100-250', label: '100–250 Kişi' },
    { value: '250+', label: '250+ Kişi' },
    { value: 'unknown', label: 'Henüz Belli Değil' },
  ];

  const serviceOptions: { value: ServiceType; label: string }[] = [
    { value: 'Fabrika', label: 'Fabrika' },
    { value: 'İnşaat / Şantiye', label: 'İnşaat / Şantiye' },
    { value: 'Şirket / Ofis', label: 'Şirket / Ofis' },
    { value: 'Okul / Kurum', label: 'Okul / Kurum' },
    { value: 'Film / Dizi Seti', label: 'Film / Dizi Seti' },
    { value: 'Organizasyon', label: 'Organizasyon' },
    { value: 'Diğer', label: 'Diğer' },
  ];

  const timingOptions: { value: Timing; label: string }[] = [
    { value: 'En kısa sürede', label: 'En kısa sürede' },
    { value: 'Bu hafta', label: 'Bu hafta' },
    { value: 'Bu ay', label: 'Bu ay' },
    { value: 'İleri tarih', label: 'İleri tarih' },
  ];

  const totalSteps = 5;

  const stepTitles = [
    'Kaç kişilik yemek hizmetine ihtiyacınız var?',
    'Hangi alanda hizmet almak istiyorsunuz?',
    'Hizmet bölgeniz neresi?',
    'Ne zaman başlamayı düşünüyorsunuz?',
    'Teklifinizi hazırlayalım',
  ];

  useEffect(() => {
    if (!isOpen) return;
    let startStep = 1;
    if (!data.personCount) startStep = 1;
    else if (!data.serviceType) startStep = 2;
    else if (!data.region.trim()) startStep = 3;
    else if (!data.timing) startStep = 4;
    else startStep = 5;
    setStep(startStep);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const canProceed = () => {
    switch (step) {
      case 1: return !!data.personCount;
      case 2: return !!data.serviceType;
      case 3: return data.region.trim().length > 0;
      case 4: return !!data.timing;
      default: return true;
    }
  };

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 bg-accent-950/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-xl md:text-2xl text-foreground-950 pr-4">
              {stepTitles[step - 1]}
            </h3>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-200 transition-colors flex-shrink-0"
            >
              <i className="ri-close-line text-2xl text-foreground-600"></i>
            </button>
          </div>

          <div className="flex items-center gap-2 mb-8">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  i + 1 <= step ? 'bg-primary-500' : 'bg-background-200'
                }`}
              ></div>
            ))}
          </div>

          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {personOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateData({ personCount: option.value })}
                  className={`py-4 px-4 rounded-xl font-label text-sm font-semibold border transition-all ${
                    data.personCount === option.value
                      ? 'bg-primary-500 text-foreground-950 border-primary-500'
                      : 'bg-background-100 text-foreground-700 border-background-200 hover:border-primary-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {serviceOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateData({ serviceType: option.value })}
                  className={`py-4 px-4 rounded-xl font-label text-sm font-semibold border transition-all ${
                    data.serviceType === option.value
                      ? 'bg-primary-500 text-foreground-950 border-primary-500'
                      : 'bg-background-100 text-foreground-700 border-background-200 hover:border-primary-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Örn: İstanbul, Anadolu Yakası"
                value={data.region}
                onChange={(e) => updateData({ region: e.target.value })}
                className="w-full px-4 py-4 rounded-xl border border-background-200 bg-background-100 text-foreground-950 font-label text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
              />
            </div>
          )}

          {step === 4 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {timingOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateData({ timing: option.value })}
                  className={`py-4 px-4 rounded-xl font-label text-sm font-semibold border transition-all ${
                    data.timing === option.value
                      ? 'bg-primary-500 text-foreground-950 border-primary-500'
                      : 'bg-background-100 text-foreground-700 border-background-200 hover:border-primary-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <div className="bg-background-100 rounded-xl p-5 space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-background-200">
                  <span className="font-label text-sm text-foreground-500">Hizmet</span>
                  <span className="font-label text-sm font-semibold text-foreground-950">{data.serviceType}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-background-200">
                  <span className="font-label text-sm text-foreground-500">Kişi Sayısı</span>
                  <span className="font-label text-sm font-semibold text-foreground-950">
                    {data.personCount === 'unknown' ? 'Henüz belli değil' : `${data.personCount} kişi`}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-background-200">
                  <span className="font-label text-sm text-foreground-500">Bölge</span>
                  <span className="font-label text-sm font-semibold text-foreground-950">{data.region}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-label text-sm text-foreground-500">Başlangıç</span>
                  <span className="font-label text-sm font-semibold text-foreground-950">{data.timing}</span>
                </div>
              </div>
              <a
                href={generateLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full text-base py-4"
                onClick={onClose}
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WHATSAPP&apos;TAN TEKLİF AL
              </a>
            </div>
          )}

          {step < 5 && (
            <div className="flex gap-3 mt-8">
              {step > 1 && (
                <button onClick={handleBack} className="btn-secondary flex-1">
                  Geri
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`btn-primary flex-1 ${
                  !canProceed() ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Devam
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}